import { Grid, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { LayersStepForm } from "./types";

import { StepColumn } from "../layout/StepColumn";
import { CUSTOM_KEY, LAYER_PRESET_ORDER, SUBLAYER_PRESET_KEYS } from "./constants";
import { AddNewChildRow, LayerCard, ObjectRow } from "./LayerStepCards";

export const LayersStepFormColumn = ({ className, form }: { className?: string; form: LayersStepForm }) => {
  const { t } = useTranslation();
  const { actions, labels, scope, ui } = form;

  const {
    activeLayerId,
    activeSubLayerId,
    customLayerNameDraft,
    editingLayerId,
    editingObjectId,
    editingSubLayerId,
    newLayerPreset,
    newSubLayerKey,
    selectedObjectId,
    subLayerDraftByLayer,
  } = ui;

  const { layerDisplayName, subLayerDisplayName } = labels;

  return (
    <StepColumn
      className={className}
      paperClassName="rounded-md h-full bg-lds-surface-bright p-8 shadow-elevation100"
      subtitle={t("AddBuilding.layersStep.subtitle") ?? ""}
      subtitleClassName="mt-2 text-lds-foreground-muted"
      title={t("AddBuilding.layersStep.title")}
    >
      <Stack className="w-full flex-1 flex-col">
        <AddNewChildRow
          addDisabled={newLayerPreset === CUSTOM_KEY && !customLayerNameDraft.trim()}
          displayOptions={LAYER_PRESET_ORDER.map((key) => t(`AddBuilding.layersStep.layerPresetLabels.${key}`))}
          dropdownValue={newLayerPreset}
          inputPlaceholder={t("AddBuilding.layersStep.customLayerNamePlaceholder") ?? ""}
          inputValue={customLayerNameDraft}
          label={t("AddBuilding.layersStep.addLayer")}
          onAdd={actions.addLayerFromForm}
          onDropdownChange={actions.setNewLayerPreset}
          onInputChange={actions.setCustomLayerNameDraft}
          options={[...LAYER_PRESET_ORDER]}
          showInput={newLayerPreset === CUSTOM_KEY}
          showOptions
        />
        <div className="max-h-[45rem] w-full  gap-6 overflow-y-auto">
          {scope.layers?.map((layer) => {
            const sublayerPresetKeys = SUBLAYER_PRESET_KEYS[layer.preset];
            const hasSublayerPresetOptions = sublayerPresetKeys.length > 0;
            const subDraft = subLayerDraftByLayer[layer.id] ?? "";
            const isLayerActive = layer.id === activeLayerId;

            const subAddDisabled = hasSublayerPresetOptions
              ? newSubLayerKey === CUSTOM_KEY
                ? !subDraft.trim()
                : false
              : !subDraft.trim();

            const addChildrenProps = !isLayerActive
              ? undefined
              : {
                  addDisabled: subAddDisabled,
                  displayOptions: hasSublayerPresetOptions
                    ? [
                        ...sublayerPresetKeys.map((k) =>
                          t(`AddBuilding.layersStep.sublayerPresetLabels.${layer.preset}.${k}`)
                        ),
                        t("AddBuilding.layersStep.customSubLayerOption"),
                      ]
                    : [],
                  dropdownValue: newSubLayerKey,
                  inputPlaceholder: t("AddBuilding.layersStep.customSubLayerPlaceholder"),
                  inputValue: subDraft,
                  onAdd: () => {
                    if (hasSublayerPresetOptions) {
                      if (newSubLayerKey === CUSTOM_KEY) {
                        actions.addSubLayer(layer.id, subDraft);
                      } else {
                        actions.addSubLayerFromPresetKey(layer, newSubLayerKey);
                      }
                    } else {
                      actions.addSubLayer(layer.id, subDraft);
                    }
                    actions.setSubDraft(layer.id, "");
                  },
                  onDropdownChange: actions.setNewSubLayerKey,
                  onInputChange: (value: string) => actions.setSubDraft(layer.id, value),
                  options: hasSublayerPresetOptions ? [...sublayerPresetKeys, CUSTOM_KEY] : [],
                  showInput: hasSublayerPresetOptions ? newSubLayerKey === CUSTOM_KEY : true,
                  showOptions: hasSublayerPresetOptions,
                };

            return (
              <LayerCard
                className="mt-4 w-full"
                editingId={editingLayerId}
                entityId={layer.id}
                footer={
                  addChildrenProps ? (
                    <AddNewChildRow {...addChildrenProps} label={t("AddBuilding.layersStep.addSubLayer")} />
                  ) : undefined
                }
                isActive={isLayerActive}
                key={layer.id}
                layer={layer}
                name={layer.name}
                onDelete={() => actions.deleteLayer(layer.id)}
                onNameChange={(name) => actions.updateLayer(layer.id, (next) => ({ ...next, name }))}
                onSelect={() => actions.setActiveLayerId(layer.id)}
                setEditingId={actions.setEditingLayerId}
                title={layerDisplayName(layer)}
                titleEditable={layer.preset === CUSTOM_KEY}
                updateLayer={actions.updateLayer}
              >
                {isLayerActive && (
                  <Grid className="w-full p-2" container spacing={2}>
                    {layer.subLayers.map((subLayer, index) => (
                      <Grid item key={subLayer.id} xs={6}>
                        <LayerCard
                          editingId={editingSubLayerId}
                          entityId={subLayer.id}
                          isActive={subLayer.id === activeSubLayerId && layer.id === activeLayerId}
                          name={subLayer.name}
                          onDelete={() => actions.deleteSubLayer(layer.id, subLayer.id)}
                          onNameChange={(name) => actions.updateSubLayerName(layer.id, subLayer.id, name)}
                          onSelect={() => actions.onSelectSubLayerRow(layer.id, subLayer.id)}
                          setEditingId={actions.setEditingSubLayerId}
                          title={subLayerDisplayName(layer, subLayer, index)}
                          titleEditable={subLayer.nameFromPreset !== true}
                        >
                          <Stack className="w-full flex-col gap-1 px-2 pb-2">
                            {subLayer.objects.length === 0 ? (
                              <p className="p-1 text-lds-foreground-muted font-ds-body-regular-xs">
                                {t("AddBuilding.layersStep.noObjectsDrawNew")}
                              </p>
                            ) : (
                              subLayer.objects.map((obj) => (
                                <ObjectRow
                                  displayLabel={obj.name.trim() || subLayerDisplayName(layer, subLayer, index)}
                                  editingObjectId={editingObjectId}
                                  isActive={
                                    layer.id === activeLayerId &&
                                    subLayer.id === activeSubLayerId &&
                                    selectedObjectId === obj.id
                                  }
                                  key={obj.id}
                                  objectId={obj.id}
                                  onDelete={() => actions.deleteObject(layer.id, subLayer.id, obj.id)}
                                  onSelect={() => actions.onSelectObjectRow(layer.id, subLayer.id, obj.id)}
                                  onUpdateName={(name) => actions.updateObjectName(layer.id, subLayer.id, obj.id, name)}
                                  rawName={obj.name}
                                  setEditingId={actions.setEditingObjectId}
                                />
                              ))
                            )}
                          </Stack>
                        </LayerCard>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </LayerCard>
            );
          })}
        </div>
      </Stack>
    </StepColumn>
  );
};
